import { Component, type ErrorInfo, type ReactNode } from 'react';
import { captureException } from '../lib/posthog';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    captureException(error, {
      componentStack: errorInfo.componentStack ?? undefined,
    });
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div className="min-h-[100dvh] bg-canvas flex items-center justify-center">
          <div className="flex flex-col items-center gap-4 max-w-sm text-center">
            <span className="material-icons-round text-red-500 text-5xl">error_outline</span>
            <h1 className="text-lg font-semibold text-ink">Something went wrong</h1>
            <p className="text-sm text-ink-secondary">
              An unexpected error occurred. Try reloading the page.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-accent text-white rounded-lg text-sm font-medium hover:bg-accent/90 transition-colors"
            >
              Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
